import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Download, Share2, MessageCircle, Send, Mail, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEMES = [
  { key: 'indigo', label: 'Indigo', color: 'bg-indigo-500' },
  { key: 'emerald', label: 'Emerald', color: 'bg-emerald-500' },
  { key: 'amber', label: 'Amber', color: 'bg-amber-500' },
  { key: 'slate', label: 'Slate', color: 'bg-slate-700' },
];

const PRICE_COLUMNS = [
  { key: 'ptr', label: 'PTR' },
  { key: 'pts', label: 'PTS' },
  { key: 'mrp', label: 'MRP' },
  { key: 'ptr_usd', label: 'PTR (USD)' },
  { key: 'pte_usd', label: 'PTE (USD)' },
];

const ProductCardDialog = ({ open, onClose, user, productName }) => {
  const [priceColumn, setPriceColumn] = useState('ptr');
  const [theme, setTheme] = useState('indigo');
  const [brand, setBrand] = useState('WINLIFE PHARMA');
  const [tagline, setTagline] = useState('Quality You Can Trust');
  const [loading, setLoading] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const cardBlobRef = useRef(null);
  const debounceRef = useRef(null);

  const generate = async () => {
    if (!productName) return;
    setLoading(true);
    if (cardUrl) {
      try { window.URL.revokeObjectURL(cardUrl); } catch (_) {}
    }
    setCardUrl(null);
    try {
      const res = await axios.post(`${API}/price-list/product-card`, {
        product_name: productName,
        price_column: priceColumn,
        brand_name: brand,
        tagline,
        theme,
      }, {
        headers: { Authorization: `Bearer ${user.token}` },
        responseType: 'blob'
      });
      cardBlobRef.current = res.data;
      const url = window.URL.createObjectURL(res.data);
      setCardUrl(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate card');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate when dialog opens, and when options change (debounced)
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(generate, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [open, productName, priceColumn, theme, brand, tagline]);

  // Cleanup on close
  useEffect(() => {
    if (!open && cardUrl) {
      try { window.URL.revokeObjectURL(cardUrl); } catch (_) {}
      setCardUrl(null);
      cardBlobRef.current = null;
    }
    // eslint-disable-next-line
  }, [open]);

  const filename = `card_${(productName || 'product').replace(/[^a-z0-9]/gi, '_').slice(0, 40)}.png`;

  const handleDownload = () => {
    if (!cardUrl) return;
    const a = document.createElement('a');
    a.href = cardUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleNativeShare = async () => {
    if (!cardBlobRef.current) return;
    try {
      const file = new File([cardBlobRef.current], filename, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: productName,
          text: `${productName} — ${brand}`,
          files: [file]
        });
      } else {
        handleDownload();
        toast.info('Native share unavailable — image downloaded');
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('Share failed');
    }
  };

  const handlePlatformShare = (platform) => {
    handleDownload();
    const text = encodeURIComponent(`${productName} — ${brand}`);
    const appUrl = encodeURIComponent(window.location.origin);
    let url = '';
    if (platform === 'whatsapp') url = `https://wa.me/?text=${text}`;
    else if (platform === 'telegram') url = `https://t.me/share/url?url=${appUrl}&text=${text}`;
    else if (platform === 'email') url = `mailto:?subject=${encodeURIComponent(productName)}&body=${text}`;
    if (url) window.open(url, '_blank');
    toast.info('Image downloaded — attach it to your message');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-[65] flex items-center justify-center p-3 sm:p-6" data-testid="product-card-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-pink-500 to-rose-500 text-white">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Product Card — Social Share
            </h3>
            <p className="text-xs text-pink-100 mt-0.5 truncate max-w-xs sm:max-w-md">
              {productName} • 1080×1080 PNG for Instagram, WhatsApp Status, Stories
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="product-card-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Preview */}
          <div className="flex-1 bg-slate-100 flex items-center justify-center p-4 min-h-[300px]">
            {loading ? (
              <div className="flex items-center gap-2 text-slate-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating card…
              </div>
            ) : cardUrl ? (
              <img
                src={cardUrl}
                alt="Product Card"
                className="max-w-full max-h-[60vh] rounded-lg shadow-lg"
                data-testid="product-card-preview"
              />
            ) : (
              <p className="text-slate-400 text-sm">Preview will appear here</p>
            )}
          </div>

          {/* Options + Share */}
          <div className="w-full md:w-72 flex flex-col bg-white border-l border-slate-200">
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Brand name</label>
                <input
                  type="text"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  className="w-full px-3 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                  data-testid="card-brand-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Tagline</label>
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  className="w-full px-3 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                  data-testid="card-tagline-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Price column</label>
                <select
                  value={priceColumn}
                  onChange={(e) => setPriceColumn(e.target.value)}
                  className="w-full px-3 py-1.5 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pink-400"
                  data-testid="card-price-col"
                >
                  {PRICE_COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Theme</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {THEMES.map(t => (
                    <button
                      key={t.key}
                      onClick={() => setTheme(t.key)}
                      data-testid={`card-theme-${t.key}`}
                      className={`h-8 rounded-md ${t.color} relative ${
                        theme === t.key ? 'ring-2 ring-offset-1 ring-slate-700' : ''
                      }`}
                      title={t.label}
                      aria-label={t.label}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 space-y-2">
              <button
                onClick={handleNativeShare}
                disabled={!cardUrl}
                data-testid="card-share-native"
                className="w-full px-3 py-2 bg-slate-800 text-white rounded-md text-sm font-medium hover:bg-slate-900 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <Share2 className="w-4 h-4" /> Native Share
              </button>
              <button
                onClick={handleDownload}
                disabled={!cardUrl}
                data-testid="card-download"
                className="w-full px-3 py-2 bg-white border border-slate-300 text-slate-800 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <Download className="w-4 h-4" /> Download PNG
              </button>
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  onClick={() => handlePlatformShare('whatsapp')}
                  disabled={!cardUrl}
                  data-testid="card-share-whatsapp"
                  className="px-2 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md disabled:opacity-50 flex items-center justify-center"
                  title="WhatsApp"
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePlatformShare('telegram')}
                  disabled={!cardUrl}
                  data-testid="card-share-telegram"
                  className="px-2 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-md disabled:opacity-50 flex items-center justify-center"
                  title="Telegram"
                >
                  <Send className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePlatformShare('email')}
                  disabled={!cardUrl}
                  data-testid="card-share-email"
                  className="px-2 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-md disabled:opacity-50 flex items-center justify-center"
                  title="Email"
                >
                  <Mail className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCardDialog;
